import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, InputBase, Popover } from "@mui/material";
import {
  Calendar,
  ChevronDown,
  Download,
  Filter,
  Plus,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import CallsColumnMenu from "./CallsColumnMenu";
import {
  formatActionLabel,
  getActionDetailsLabel,
  renderActionFilterItem,
} from "./callCellRenderers";
import { FILTERABLE_COLUMNS } from "./callsTableConfig";
import { formatPhoneNumber } from "../utils/formatnumber";

const DATE_PRESETS = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
];

const formatFilterDisplay = (columnId, value) => {
  if (columnId === "action") return formatActionLabel(value);
  if (columnId === "actionDetails") return getActionDetailsLabel(value);
  if (columnId === "toNumber" || columnId === "callerNumber") {
    return formatPhoneNumber(value);
  }
  return value || "(empty)";
};

const summarizeFilterValue = (columnId, value, filterType) => {
  if (filterType === "multi") {
    if (!Array.isArray(value) || value.length === 0) return null;
    if (value.length === 1) return formatFilterDisplay(columnId, value[0]);
    if (value.length === 2) {
      return value.map((v) => formatFilterDisplay(columnId, v)).join(", ");
    }
    return `${value.length} selected`;
  }
  if (filterType === "boolean" || filterType === "search") {
    return value ? String(value) : null;
  }
  return null;
};

const toolbarBtn =
  "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 max-w-[280px]";

/** Value picker for one active filter field */
const FilterValuePopover = ({
  open,
  anchorEl,
  onClose,
  col,
  options,
  value,
  onChange,
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open, col?.id]);

  if (!col) return null;

  const filteredOptions = (options || []).filter((o) =>
    String(formatFilterDisplay(col.id, o))
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            width: 280,
            maxHeight: 360,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {col.label}
      </div>

      {col.filterType === "boolean" && (
        <div className="p-2 space-y-0.5">
          {[
            { v: "", label: "Any" },
            { v: "Yes", label: "Yes" },
            { v: "No", label: "No" },
          ].map((opt) => (
            <button
              key={opt.v || "any"}
              type="button"
              onClick={() => {
                onChange(opt.v);
                if (opt.v) onClose();
              }}
              className={`w-full text-left px-2.5 py-2 text-sm rounded-md ${
                (value || "") === opt.v
                  ? "bg-indigo-50 text-indigo-700 font-medium"
                  : "hover:bg-gray-50 text-gray-800"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {col.filterType === "search" && (
        <div className="p-3">
          <div className="flex items-center gap-2 px-2.5 py-2 border border-gray-200 rounded-lg">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <InputBase
              autoFocus
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`Search ${col.label}…`}
              sx={{ fontSize: 13, flex: 1 }}
            />
            {value ? (
              <button
                type="button"
                onClick={() => onChange("")}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      )}

      {col.filterType === "multi" && (
        <>
          {(options || []).length > 6 && (
            <div className="px-3 pt-2">
              <div className="flex items-center gap-2 px-2.5 py-1.5 border border-gray-200 rounded-lg">
                <Search className="w-3.5 h-3.5 text-gray-400" />
                <InputBase
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  sx={{ fontSize: 13, flex: 1 }}
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto max-h-[260px] p-2">
            {filteredOptions.slice(0, 80).map((opt) => {
              const selected = Array.isArray(value) && value.includes(opt);
              return (
                <label
                  key={String(opt)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <Checkbox
                    size="small"
                    checked={!!selected}
                    onChange={() => {
                      const prev = Array.isArray(value) ? value : [];
                      onChange(
                        selected
                          ? prev.filter((v) => v !== opt)
                          : [...prev, opt]
                      );
                    }}
                    sx={{ p: 0.25 }}
                  />
                  <span className="text-sm text-gray-800 truncate min-w-0">
                    {col.id === "action"
                      ? renderActionFilterItem(opt)
                      : formatFilterDisplay(col.id, opt)}
                  </span>
                </label>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="text-xs text-gray-400 px-2 py-3">No options</div>
            )}
          </div>
          {Array.isArray(value) && value.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {value.length} selected
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            </div>
          )}
        </>
      )}
    </Popover>
  );
};

const CallsFilterPane = ({
  table,
  calls,
  dateRange,
  onDatePresetChange,
  startDate,
  endDate,
  onCustomDateChange,
  columnOrder,
  onColumnOrderChange,
  onExport,
}) => {
  const [datesAnchor, setDatesAnchor] = useState(null);
  const [draftStart, setDraftStart] = useState(startDate || "");
  const [draftEnd, setDraftEnd] = useState(endDate || "");

  // Filters pinned to the bar as their own dropdowns
  const [enabledFilters, setEnabledFilters] = useState([]);
  const [addAnchor, setAddAnchor] = useState(null);
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [activeFilterAnchor, setActiveFilterAnchor] = useState(null);

  const columnFilters = table.getState().columnFilters;
  const filterable = FILTERABLE_COLUMNS;
  const filterableById = useMemo(
    () => Object.fromEntries(FILTERABLE_COLUMNS.map((c) => [c.id, c])),
    []
  );

  // Sync: if filters exist from table state but aren't enabled yet, enable them
  useEffect(() => {
    const ids = columnFilters.map((f) => f.id);
    if (!ids.length) return;
    setEnabledFilters((prev) => {
      const missing = ids.filter((id) => !prev.includes(id));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [columnFilters]);

  const uniqueValues = useMemo(() => {
    const map = {};
    FILTERABLE_COLUMNS.forEach((col) => {
      if (col.filterType === "search" || col.filterType === "boolean") return;
      const set = new Set();
      (calls || []).forEach((call) => {
        const v = call[col.accessorKey];
        if (v != null && v !== "") set.add(v);
      });
      map[col.id] = Array.from(set).sort((a, b) =>
        String(a).localeCompare(String(b))
      );
    });
    return map;
  }, [calls]);

  const getFilterValue = (id) => {
    const found = columnFilters.find((f) => f.id === id);
    return found?.value;
  };

  const setFilterValue = (id, value) => {
    const empty =
      value == null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    table.setColumnFilters((prev) => {
      const rest = prev.filter((f) => f.id !== id);
      if (empty) return rest;
      return [...rest, { id, value }];
    });
  };

  const availableToAdd = filterable.filter(
    (c) => !enabledFilters.includes(c.id)
  );

  const removeFilter = (id) => {
    setEnabledFilters((prev) => prev.filter((x) => x !== id));
    setFilterValue(id, colEmptyValue(filterableById[id]?.filterType));
    if (activeFilterId === id) {
      setActiveFilterId(null);
      setActiveFilterAnchor(null);
    }
  };

  const resetFilters = () => {
    table.resetColumnFilters();
    setEnabledFilters([]);
    setActiveFilterId(null);
    setActiveFilterAnchor(null);
  };

  const openFilterDropdown = (id, el) => {
    if (activeFilterId === id) {
      setActiveFilterId(null);
      setActiveFilterAnchor(null);
      return;
    }
    setActiveFilterId(id);
    setActiveFilterAnchor(el);
  };

  const formatShortDate = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const dateButtonLabel =
    dateRange != null
      ? DATE_PRESETS.find((p) => p.value === dateRange)?.label || "Custom range"
      : startDate && endDate
        ? `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`
        : "Custom range";

  const applyCustomDates = () => {
    if (!draftStart || !draftEnd) return;
    if (new Date(draftStart) > new Date(draftEnd)) return;
    onCustomDateChange?.(draftStart, draftEnd);
    setDatesAnchor(null);
  };

  const hasActiveValues = columnFilters.some((f) => {
    if (Array.isArray(f.value)) return f.value.length > 0;
    return Boolean(f.value);
  });

  const activeCol = activeFilterId ? filterableById[activeFilterId] : null;

  return (
    <div className="border-b border-gray-200">
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Filters wrap in the left zone only */}
        <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
          {/* Date */}
          <button
            type="button"
            onClick={(e) => {
              setDraftStart(startDate || "");
              setDraftEnd(endDate || "");
              setDatesAnchor(e.currentTarget);
            }}
            className={toolbarBtn}
          >
            <Calendar className="w-4 h-4 shrink-0" />
            <span className="truncate">{dateButtonLabel}</span>
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          </button>

          {/* Enabled filters — each is its own dropdown trigger */}
          {enabledFilters.map((id) => {
            const col = filterableById[id];
            if (!col) return null;
            const value = getFilterValue(id);
            const summary = summarizeFilterValue(id, value, col.filterType);
            const isOpen = activeFilterId === id;

            return (
              <div
                key={id}
                className={`inline-flex items-center rounded-lg border max-w-[300px] ${
                  summary || isOpen
                    ? "border-indigo-200 bg-indigo-50/60"
                    : "border-gray-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  data-filter-id={id}
                  onClick={(e) => openFilterDropdown(id, e.currentTarget)}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-2 text-sm font-medium text-gray-800 min-w-0"
                >
                  <span className="text-gray-500 shrink-0">{col.label}</span>
                  {summary ? (
                    <>
                      <span className="text-gray-400 shrink-0">is</span>
                      <span className="truncate font-semibold text-gray-900">
                        {summary}
                      </span>
                    </>
                  ) : null}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => removeFilter(id)}
                  className="pr-2 pl-0.5 py-2 text-gray-400 hover:text-gray-700"
                  aria-label={`Remove ${col.label} filter`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}

          {/* Add filter */}
          {availableToAdd.length > 0 && (
            <button
              type="button"
              onClick={(e) => setAddAnchor(e.currentTarget)}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50"
            >
              <Filter className="w-3.5 h-3.5" />
              <Plus className="w-3.5 h-3.5" />
              <span>Add filter</span>
            </button>
          )}
        </div>

        {/* Actions stay pinned top-right; filters wrap before this column */}
        <div className="flex items-center gap-2 shrink-0 pt-0">
          <CallsColumnMenu
            table={table}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
          />

          {(enabledFilters.length > 0 || hasActiveValues) && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </button>
          )}

          <button type="button" onClick={onExport} className={toolbarBtn}>
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date popover */}
      <Popover
        open={Boolean(datesAnchor)}
        anchorEl={datesAnchor}
        onClose={() => setDatesAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              border: "1px solid #E5E7EB",
              p: 2,
              width: 300,
            },
          },
        }}
      >
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
          Presets
        </div>
        <div className="space-y-1 mb-3">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => {
                onDatePresetChange?.(p.value);
                setDatesAnchor(null);
              }}
              className={`w-full text-left px-2 py-1.5 text-sm rounded-md ${
                dateRange != null && dateRange === p.value
                  ? "bg-indigo-50 text-indigo-700 font-medium"
                  : "hover:bg-gray-50 text-gray-800"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
          Custom range
        </div>
        <div className="flex flex-col gap-2">
          <input
            type="date"
            value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm"
          />
          <input
            type="date"
            value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={applyCustomDates}
            className="mt-1 px-3 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90"
          >
            Apply dates
          </button>
        </div>
      </Popover>

      {/* Add filter — field picker */}
      <Popover
        open={Boolean(addAnchor)}
        anchorEl={addAnchor}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              width: 240,
              maxHeight: 360,
              overflowY: "auto",
              py: 1,
            },
          },
        }}
      >
        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Add filter
        </div>
        {availableToAdd.map((col) => (
          <button
            key={col.id}
            type="button"
            onClick={() => {
              const addBtn = addAnchor;
              setEnabledFilters((prev) =>
                prev.includes(col.id) ? prev : [...prev, col.id]
              );
              setAddAnchor(null);
              // After chip mounts, open its dropdown — use rAF + querySelector
              requestAnimationFrame(() => {
                const chip = document.querySelector(
                  `[data-filter-id="${col.id}"]`
                );
                setActiveFilterId(col.id);
                setActiveFilterAnchor(chip || addBtn);
              });
            }}
            className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 hover:text-indigo-800"
          >
            {col.label}
          </button>
        ))}
      </Popover>

      {/* Per-filter value dropdown */}
      <FilterValuePopover
        open={Boolean(activeFilterId && activeFilterAnchor)}
        anchorEl={activeFilterAnchor}
        onClose={() => {
          setActiveFilterId(null);
          setActiveFilterAnchor(null);
        }}
        col={activeCol}
        options={activeFilterId ? uniqueValues[activeFilterId] || [] : []}
        value={activeFilterId ? getFilterValue(activeFilterId) : undefined}
        onChange={(v) => {
          if (activeFilterId) setFilterValue(activeFilterId, v);
        }}
      />
    </div>
  );
};

function colEmptyValue(filterType) {
  if (filterType === "multi") return [];
  return "";
}

export default CallsFilterPane;
